/**
 * Operaciones de edición sobre el `contentEditable` del `EditorInput`.
 *
 * Todo pasa por `document.execCommand`. Está marcado como obsoleto pero sigue
 * siendo lo único que aplica formato respetando la selección sin traerse un
 * motor de edición entero (ProseMirror y compañía), y aquí no se pueden añadir
 * dependencias: la librería se publica y este entorno no descarga paquetes.
 *
 * La regla que sostiene el resto: **`styleWithCSS` activado**. Sin él el
 * navegador escribe `<font color>` y `<font size>`, etiquetas que la política de
 * saneado tira, y el formato se perdía al guardar. Con él escribe
 * `<span style="…">`, que sí sobrevive.
 */

/** Tamaños de letra ofrecidos en la barra (en px). */
export const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 30, 36, 48, 60, 72] as const

/** Tipografías: sólo familias que existen en cualquier sistema. */
export const FONT_FAMILIES = [
  { value: '', title: 'Predeterminada' },
  { value: 'Arial, Helvetica, sans-serif', title: 'Arial' },
  { value: 'Georgia, serif', title: 'Georgia' },
  { value: '"Times New Roman", Times, serif', title: 'Times New Roman' },
  { value: '"Courier New", Courier, monospace', title: 'Courier New' },
  { value: 'Verdana, Geneva, sans-serif', title: 'Verdana' },
  { value: '"Trebuchet MS", Helvetica, sans-serif', title: 'Trebuchet MS' },
  { value: 'system-ui, sans-serif', title: 'Sistema' }
] as const

/** Paleta del selector de color: la de marca más una escala neutra. */
export const COLOR_SWATCHES = [
  '#000000',
  '#374151',
  '#6b7280',
  '#9ca3af',
  '#d1d5db',
  '#ffffff',
  '#d13a44',
  '#dc2626',
  '#ea580c',
  '#d97706',
  '#65a30d',
  '#16a34a',
  '#0d9488',
  '#0284c7',
  '#2563eb',
  '#4f46e5',
  '#7c3aed',
  '#c026d3'
] as const

const BLOCK_TAGS = ['p', 'div', 'li', 'blockquote', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']

/** Activa el modo CSS una sola vez por documento (ver cabecera del módulo). */
let styleWithCssReady = false
export function ensureStyleWithCss(): void {
  if (styleWithCssReady) return
  try {
    document.execCommand('styleWithCSS', false, 'true')
    // Al pulsar Enter el navegador crea `<p>` en vez de `<div>`: los `div`
    // sueltos se cuelan en el HTML guardado y rompen los márgenes del texto.
    document.execCommand('defaultParagraphSeparator', false, 'p')
    styleWithCssReady = true
  } catch {
    // Navegador que no lo admite: el saneado se encarga de lo que no valga.
  }
}

export function exec(command: string, value?: string): boolean {
  ensureStyleWithCss()
  try {
    return document.execCommand(command, false, value)
  } catch {
    return false
  }
}

export function queryState(command: string): boolean {
  try {
    return document.queryCommandState(command)
  } catch {
    return false
  }
}

export function queryValue(command: string): string {
  try {
    return document.queryCommandValue(command) ?? ''
  } catch {
    return ''
  }
}

/** Rango actual, sólo si está dentro del editor indicado. */
export function rangeInside(editor: HTMLElement | undefined): Range | null {
  if (!editor) return null
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  const node = range.commonAncestorContainer
  return editor.contains(node.nodeType === 1 ? node : (node.parentNode as Node)) ? range : null
}

/** Elemento (no nodo de texto) donde está el cursor. */
export function currentElement(editor: HTMLElement | undefined): HTMLElement | null {
  const range = rangeInside(editor)
  if (!range) return null
  const node = range.startContainer
  return (node.nodeType === 1 ? node : node.parentNode) as HTMLElement | null
}

/** Bloque contenedor del cursor (`p`, `li`, `h2`…), sin salir del editor. */
export function currentBlock(editor: HTMLElement | undefined): HTMLElement | null {
  let node: Node | null = currentElement(editor)
  while (node && node !== editor) {
    if (node.nodeType === 1 && BLOCK_TAGS.includes((node as HTMLElement).tagName.toLowerCase())) {
      return node as HTMLElement
    }
    node = node.parentNode
  }
  return null
}

/** Ancestro con la etiqueta pedida (`a`, `li`, `blockquote`…), sin salir del editor. */
export function closestTag(editor: HTMLElement | undefined, tag: string): HTMLElement | null {
  let node: Node | null = currentElement(editor)
  const wanted = tag.toLowerCase()
  while (node && node !== editor) {
    if (node.nodeType === 1 && (node as HTMLElement).tagName.toLowerCase() === wanted) return node as HTMLElement
    node = node.parentNode
  }
  return null
}

/** Guarda el rango para poder restaurarlo tras pasar por un diálogo o un menú. */
export function saveRange(editor: HTMLElement | undefined): Range | null {
  const range = rangeInside(editor)
  return range ? range.cloneRange() : null
}

export function restoreRange(editor: HTMLElement | undefined, range: Range | null): void {
  if (!editor) return
  editor.focus()
  if (!range) return
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}

/** Coloca el cursor al final del contenido. */
export function caretToEnd(editor: HTMLElement | undefined): void {
  if (!editor) return
  const range = document.createRange()
  range.selectNodeContents(editor)
  range.collapse(false)
  const sel = window.getSelection()
  sel?.removeAllRanges()
  sel?.addRange(range)
}

/**
 * Aplica un tamaño de letra a la selección.
 *
 * `execCommand('fontSize')` sólo entiende la escala 1-7 de HTML, así que se usa
 * el 7 como marca y se sustituye por un `span` con el tamaño real en píxeles.
 * Es el camino habitual para tener tamaños libres sin perder la selección.
 */
export function applyFontSize(editor: HTMLElement | undefined, px: number): void {
  if (!editor) return
  exec('fontSize', '7')
  const marked = editor.querySelectorAll<HTMLElement>('font[size="7"], span[style*="xxx-large"]')
  for (const node of Array.from(marked)) {
    const span = document.createElement('span')
    span.style.fontSize = `${px}px`
    span.innerHTML = node.innerHTML
    node.replaceWith(span)
  }
}

/** Tamaño de letra efectivo donde está el cursor, en px. */
export function currentFontSize(editor: HTMLElement | undefined): number | null {
  const el = currentElement(editor)
  if (!el || !editor?.contains(el)) return null
  const size = window.getComputedStyle(el).fontSize
  const value = Number.parseFloat(size)
  return Number.isFinite(value) ? Math.round(value) : null
}

/** Familia tipográfica efectiva donde está el cursor. */
export function currentFontFamily(editor: HTMLElement | undefined): string {
  const el = currentElement(editor)
  if (!el || !editor?.contains(el)) return ''
  const family = window.getComputedStyle(el).fontFamily
  const match = FONT_FAMILIES.find(f => f.value && family.toLowerCase().startsWith(f.value.split(',')[0].toLowerCase().replace(/"/g, '')))
  return match?.value ?? ''
}

/** Inserta HTML ya saneado en el punto del cursor. */
export function insertHtml(html: string): void {
  exec('insertHTML', html)
}

/** Envuelve la selección (o inserta) un enlace con `rel` seguro. */
export function applyLink(text: string, href: string, newTab: boolean): void {
  const safeText = text.replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const rel = newTab ? ' target="_blank" rel="noopener noreferrer"' : ''
  insertHtml(`<a href="${href.replace(/"/g, '&quot;')}"${rel}>${safeText}</a>`)
}

/** Bloque de cita: alterna entre `blockquote` y párrafo. */
export function toggleBlockquote(editor: HTMLElement | undefined): void {
  const quote = closestTag(editor, 'blockquote')
  exec('formatBlock', quote ? '<p>' : '<blockquote>')
}

/** Bloque de código monoespaciado. */
export function insertCodeBlock(): void {
  const selected = window.getSelection()?.toString() ?? ''
  const content = selected || 'código'
  insertHtml(`<pre><code>${content.replace(/</g, '&lt;')}</code></pre>`)
}

/** Código en línea sobre la selección. */
export function insertInlineCode(): void {
  const selected = window.getSelection()?.toString() ?? ''
  if (!selected) return
  insertHtml(`<code>${selected.replace(/</g, '&lt;')}</code>`)
}

/**
 * Sangría dentro de listas: es lo que convierte una lista en anidada.
 * Fuera de una lista, mueve el margen del bloque.
 */
export function indent(): void {
  exec('indent')
}

export function outdent(): void {
  exec('outdent')
}

/**
 * Saca las listas que el navegador deja **dentro** de un párrafo.
 *
 * Al convertir en lista un párrafo vacío, Chrome produce `<p><ul>…</ul></p>`:
 * marcado inválido que descoloca los márgenes y que ningún saneado arregla,
 * porque las dos etiquetas están permitidas por separado.
 */
export function unwrapListsFromParagraphs(editor: HTMLElement | undefined): void {
  if (!editor) return
  const contenedoresInvalidos = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']
  for (const list of Array.from(editor.querySelectorAll('ul, ol'))) {
    const padre = list.parentElement
    if (!padre || padre === editor) continue
    // Una lista dentro de otra lista sí es válida: eso es una lista anidada.
    if (!contenedoresInvalidos.includes(padre.tagName.toLowerCase())) continue
    padre.replaceWith(list)
  }
}

/** Palabras y caracteres del contenido, para el pie del editor. */
export function countText(html: string): { words: number; chars: number } {
  const text = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return { words: text ? text.split(' ').length : 0, chars: text.length }
}
